// Redux Saga
import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { terminalApi } from 'services/apiDigitalTicket/terminal';

// Types
import { AddressInterface } from 'common/address.interface';
// Contants
import { errorMessagersList } from 'helpers/errorMessagers';
import { push } from 'connected-react-router';
import PATH from 'constants/basePath';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import * as TerminalActions from './actions';

// Types
import { TerminalsTypes, TerminalInterface } from './types';

const formatAddress = (address: AddressInterface | undefined) => {
  const street = address?.street || '';
  const city = address?.city || '';
  const cep = address?.cep || '';

  // eslint-disable-next-line
  return `${street} ${city && ' - ' + city} ${cep && ' - ' + cep}`;
};

/* eslint-disable no-console */
function* list({
  payload,
}: ReturnType<typeof TerminalActions.listTerminalsRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando terminais' }),
      );
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(terminalApi.list, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        TerminalActions.listTerminalsSuccess({
          // prettier-ignore
          terminalList: (Array.isArray(response.data) ? response.data : [response.data]).map((terminal: TerminalInterface) => ({
            ...terminal,
            address: formatAddress(terminal.Stop ? terminal.Stop.Address : undefined),
          })),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      if (!filtering) {
        yield put(DialogsActions.setClose());
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminals - listTerminals - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalActions.listTerminalsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof TerminalActions.getTerminalRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando terminal' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(terminalApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalActions.getTerminalSuccess({
          terminalData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminals - getTerminal - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalActions.getTerminalFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof TerminalActions.filterTerminalsRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(terminalApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalActions.filterTerminalsSuccess({
          // prettier-ignore
          terminalList: response.data.map((terminal: TerminalInterface) => ({
            ...terminal,
            address: formatAddress(terminal.Stop ? terminal.Stop.Address : undefined),
          })),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      meta.promise.resolve(response.data);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminals - filterTerminals - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    meta.promise.reject(error);
    yield put(TerminalActions.filterTerminalsFailure());
  }
}

function* create({
  payload,
}: ReturnType<typeof TerminalActions.createTerminalRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando terminal' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(terminalApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalActions.createTerminalSuccess({
          terminalData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Terminal criado com sucesso',
        }),
      );
      yield put(
        TerminalActions.listTerminalsRequest({
          body: {
            _includes: 'block,address',
          },
        }),
      );
      yield put(push(PATH.TERMINAL_MANAGEMENT.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminals - createTerminal - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalActions.createTerminalFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof TerminalActions.updateTerminalRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(terminalApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TerminalActions.updateTerminalSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Terminal alterado com sucesso',
        }),
      );
      yield put(
        TerminalActions.listTerminalsRequest({
          body: {
            _includes: 'block,address',
          },
        }),
      );

      if (payload.refresh) window.location.reload();
      yield put(push(PATH.TERMINAL_MANAGEMENT.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminals - updateTerminal - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalActions.updateTerminalFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof TerminalActions.deleteTerminalRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo terminal' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(terminalApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Terminal excluído com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        TerminalActions.listTerminalsRequest({
          body: {
            _includes: 'block,address',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminals - deleteTerminal - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalActions.deleteTerminalFailure());
    yield put(DialogsActions.setClose());
  }
}

function* gateUpdate({
  payload,
  meta,
}: ReturnType<typeof TerminalActions.gateUpdateRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(terminalApi.gateUpdate, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      if (meta?.promise?.resolve) {
        meta.promise.resolve(response);
      }
      
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Gate alterado com sucesso',
        }),
      );
      yield put(
        TerminalActions.listTerminalsRequest({
          body: {
            id: payload.terminalId,
            _includes: 'block,address,linestop',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('Gate - updateate - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalActions.updateTerminalFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listConfig({
  payload,
}: ReturnType<typeof TerminalActions.listTerminalConfigRequest>): any {
  try {
    const { body, filtering } = payload;

    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando configurações' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(terminalApi.listConfig, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        TerminalActions.listTerminalConfigSuccess({
          terminalConfigList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('terminals - listTerminalConfig - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TerminalActions.listTerminalConfigFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateConfig({
  payload,
}: ReturnType<typeof TerminalActions.updateTerminalConfigRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(terminalApi.updateConfig, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TerminalActions.updateTerminalConfigSuccess({
          terminalConfigData: response.data,
        }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Configurações atualizadas com sucesso.',
        }),
      );

      yield put(DialogsActions.setClose());
      yield put(TerminalActions.listTerminalsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('schoolSettings - updateTerminalConfig - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TerminalActions.updateTerminalConfigFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const terminalsSagas = [
  takeLatest(TerminalsTypes.LIST_TERMINALS_REQUEST, list),
  takeLatest(TerminalsTypes.GET_TERMINAL_REQUEST, get),
  takeLatest(TerminalsTypes.FILTER_TERMINALS_REQUEST, filter),
  takeLatest(TerminalsTypes.CREATE_TERMINAL_REQUEST, create),
  takeLatest(TerminalsTypes.UPDATE_TERMINAL_REQUEST, update),
  takeLatest(TerminalsTypes.DELETE_TERMINAL_REQUEST, remove),
  takeLatest(TerminalsTypes.GATE_UPDATE_REQUEST, gateUpdate),
  takeLatest(TerminalsTypes.LIST_TERMINAL_CONFIG_REQUEST, listConfig),
  takeLatest(TerminalsTypes.UPDATE_TERMINAL_CONFIG_REQUEST, updateConfig),
];
